'use client';

import {
  ComponentType,
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { DefaultToolGroup } from '@/components/assistant-ui/default-tool-group';
import type {
  DoneLabelResolver,
  GetTrailConfigFn,
} from '@/components/assistant-ui/trail-tool-config';
import type { ToolGroupProps } from '@/components/assistant-ui/use-tool-group-state';
import { useConfigValue } from '@/components/providers/config-provider';

export type ToolGroupComponent = ComponentType<ToolGroupProps>;
export type IsHandledToolFn = (toolName: string) => boolean;

export interface ToolGroupModule {
  ToolGroupComponent: ToolGroupComponent;
  isHandledTool: IsHandledToolFn;
  getTrailConfig?: GetTrailConfigFn;
  /** Tenant-level resolver for the trail's final-row label. See `DoneLabelResolver`. */
  resolveDoneLabel?: DoneLabelResolver;
  /**
   * Per-locale message namespace consulted as a deep fallback for the trail's
   * final-row copy (`streamingDoneByBucket`). When set, the trail reads
   * `${bucketCatalogNamespace}.${bucket}` (e.g. `hotelTrailUI.postSearch`) if
   * the tenant config doesn't override the bucket inline. Loaded only for
   * tenants that include the namespace in `translationNamespaces`, so it
   * never enters non-domain bundles.
   */
  bucketCatalogNamespace?: string;
}

const DEFAULT_CONTEXT: ToolGroupModule = {
  ToolGroupComponent: DefaultToolGroup,
  isHandledTool: () => false,
};

const ToolGroupContext = createContext<ToolGroupModule>(DEFAULT_CONTEXT);

/**
 * Module-level cache for tool group loading promises.
 * Shared between `preloadToolGroups` (eager) and `ToolGroupProvider` (lazy),
 * so that preloaded modules are available instantly when the provider mounts.
 */
const moduleCache = new Map<number, Promise<ToolGroupModule | null>>();

/**
 * Synchronous cache of already-resolved modules.
 * Populated when promises resolve, checked by `ToolGroupProvider` on mount
 * to avoid a flash of fallback UI when modules were preloaded.
 */
const resolvedCache = new Map<number, ToolGroupModule | null>();

/**
 * Loads a tool group by numeric ID, returning a cached promise if available.
 * Each case uses explicit imports to ensure proper code splitting.
 * Template literal imports would bundle ALL tool groups together.
 */
function loadToolGroupById(toolGroupId: number): Promise<ToolGroupModule | null> {
  const cached = moduleCache.get(toolGroupId);
  if (cached) return cached;

  const promise = loadToolGroupByIdUncached(toolGroupId).then(mod => {
    resolvedCache.set(toolGroupId, mod);
    return mod;
  });
  moduleCache.set(toolGroupId, promise);
  return promise;
}

/**
 * Attempts to synchronously resolve tool group modules from the resolved cache.
 * Returns the composed module if ALL requested IDs are already resolved, or null otherwise.
 */
function tryResolveSync(ids: number[]): ToolGroupModule | null {
  if (ids.length === 0) return null;
  if (!ids.every(id => resolvedCache.has(id))) return null;

  const modules = ids
    .map(id => resolvedCache.get(id))
    .filter((m): m is ToolGroupModule => m !== null);

  return modules.length > 0 ? composeModules(modules) : null;
}

async function loadToolGroupByIdUncached(toolGroupId: number): Promise<ToolGroupModule | null> {
  try {
    switch (toolGroupId) {
      case 1:
        return await import('@/components/assistant-ui/tool-groups/t1-tool-group');
      case 2:
        return await import('@/components/assistant-ui/tool-groups/t2-tool-group');
      case 4:
        return await import('@/components/assistant-ui/tool-groups/t4-tool-group');
      case 5:
        return await import('@/components/assistant-ui/tool-groups/t5-tool-group');
      case 6:
        return await import('@/components/assistant-ui/tool-groups/t6-tool-group');
      case 7:
        return await import('@/components/assistant-ui/tool-groups/t7-tool-group');
      case 8:
        return await import('@/components/assistant-ui/tool-groups/t8-tool-group');
      case 9:
        return await import('@/components/assistant-ui/tool-groups/t9-tool-group');
      case 10:
        return await import('@/components/assistant-ui/tool-groups/t10-tool-group');
      case 11:
        return await import('@/components/assistant-ui/tool-groups/t11-tool-group');
      case 12:
        return await import('@/components/assistant-ui/tool-groups/t12-tool-group');
      case 13:
        return await import('@/components/assistant-ui/tool-groups/t13-tool-group');
      case 14:
        return await import('@/components/assistant-ui/tool-groups/t14-tool-group');
      case 15:
        return await import('@/components/assistant-ui/tool-groups/t15-tool-group');
      case 16:
        return await import('@/components/assistant-ui/tool-groups/t16-tool-group');
      case 17:
        return await import('@/components/assistant-ui/tool-groups/t17-tool-group');
      case 18:
        return await import('@/components/assistant-ui/tool-groups/t16-gb-tool-group');
      case 19:
        return await import('@/components/assistant-ui/tool-groups/t19-tool-group');
      case 20:
        return await import('@/components/assistant-ui/tool-groups/t20-tool-group');
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/**
 * Eagerly starts loading tool group chunks. Call this before the provider mounts
 * to eliminate the flash caused by lazy loading.
 *
 * @example
 * // At module scope or in a parent component
 * preloadToolGroups([5, 10]); // T5 + T10
 */
export function preloadToolGroups(ids: number[]): void {
  ids.forEach(id => loadToolGroupById(id));
}

/**
 * Composes multiple tool group modules into a single module.
 *
 * - `isHandledTool` returns true if ANY module claims the tool name.
 * - `ToolGroupComponent` renders each module's component in sequence.
 *   Only the LAST module receives `children` — this is intentional because
 *   each ToolGroupComponent wraps AssistantUI's `<ToolFallback>` pattern,
 *   and only the final one should render the unhandled-tool fallback.
 */
function composeModules(modules: ToolGroupModule[]): ToolGroupModule {
  if (modules.length === 0) return DEFAULT_CONTEXT;
  if (modules.length === 1) return modules[0];

  const isHandledTool: IsHandledToolFn = (toolName: string) =>
    modules.some(m => m.isHandledTool(toolName));

  const getTrailConfig: GetTrailConfigFn = (toolName: string) => {
    for (const m of modules) {
      const config = m.getTrailConfig?.(toolName);
      if (config) return config;
    }
    return undefined;
  };

  // First module that returns a string wins. Lets a tenant carry the tenant-
  // specific resolver while a co-mounted module (e.g. shared hotels group)
  // contributes only via bucket variants.
  const resolveDoneLabel: DoneLabelResolver | undefined = modules.some(m => m.resolveDoneLabel)
    ? ctx => {
        for (const m of modules) {
          const label = m.resolveDoneLabel?.(ctx);
          if (label) return label;
        }
        return undefined;
      }
    : undefined;

  function ComposedToolGroup({
    startIndex,
    endIndex,
    children,
    renderMode,
  }: ToolGroupProps): ReactNode {
    return (
      <>
        {modules.map((mod, i) => {
          const Comp = mod.ToolGroupComponent;
          const isLast = i === modules.length - 1;
          return (
            <Comp key={i} startIndex={startIndex} endIndex={endIndex} renderMode={renderMode}>
              {isLast ? children : undefined}
            </Comp>
          );
        })}
      </>
    );
  }

  const bucketCatalogNamespace = modules.find(
    m => m.bucketCatalogNamespace,
  )?.bucketCatalogNamespace;

  return {
    ToolGroupComponent: ComposedToolGroup,
    isHandledTool,
    getTrailConfig,
    resolveDoneLabel,
    bucketCatalogNamespace,
  };
}

interface ToolGroupProviderProps {
  children: ReactNode;
}

export function ToolGroupProvider({ children }: ToolGroupProviderProps) {
  const toolGroupId = useConfigValue('toolGroupId') as number | undefined;
  const toolGroupIds = useConfigValue('toolGroupIds') as number[] | undefined;

  // Determine which IDs to load: prefer array (multi-group), fall back to single
  const idsToLoad = useMemo(() => {
    if (toolGroupIds && toolGroupIds.length > 0) return toolGroupIds;
    if (toolGroupId) return [toolGroupId];
    return [];
  }, [toolGroupId, toolGroupIds]);

  // Try to resolve synchronously from cache (avoids flash when preloaded)
  const [toolGroupModule, setToolGroupModule] = useState<ToolGroupModule | null>(() =>
    tryResolveSync(idsToLoad),
  );

  const idsKey = idsToLoad.join(',');

  // When IDs change, immediately resolve from cache to avoid a render with stale state.
  // This is React's recommended pattern for derived state (render-time setState).
  const [prevIdsKey, setPrevIdsKey] = useState(idsKey);
  if (idsKey !== prevIdsKey) {
    setPrevIdsKey(idsKey);
    setToolGroupModule(tryResolveSync(idsToLoad));
  }

  useEffect(() => {
    if (idsToLoad.length === 0) return;
    if (tryResolveSync(idsToLoad)) return;

    let cancelled = false;

    Promise.all(idsToLoad.map(id => loadToolGroupById(id))).then(results => {
      if (cancelled) return;
      const loaded = results.filter((m): m is ToolGroupModule => m !== null);
      if (loaded.length > 0) {
        setToolGroupModule(composeModules(loaded));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [idsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const value = useMemo<ToolGroupModule>(
    () => toolGroupModule ?? DEFAULT_CONTEXT,
    [toolGroupModule],
  );

  return <ToolGroupContext.Provider value={value}>{children}</ToolGroupContext.Provider>;
}

export function useToolGroup(): ToolGroupModule {
  return useContext(ToolGroupContext);
}
